import {
    Injectable, inject
} from "@angular/core";
import {
    Observable
} from "rxjs";
import {
    map
} from "rxjs/operators";
import {
    StringUtil
} from "@shopliftr/common-js/data";
import {
    Category, CategorySearchFilterOptions
} from "@shopliftr/common-js/shared";
import { IAutocompleteController } from "../../interfaces/autocomplete-controller";
import { IAutocompleteResults } from "../../interfaces/autocomplete-results";
import { IAutocompleteOption } from "../../interfaces/autocomplete-option";
import { ICategoryAutocompleteOptions } from "./interfaces/category-autocomplete-options";
import { CategoryApiClientService } from "@shopliftr/common-ng";

/**
 * Autocomplete controller for Category entities
 * Provides autocomplete functionality with client-side filtering
 */
@Injectable({
    providedIn: "root"
})
export class CategoryAutocompleteControllerService implements IAutocompleteController<Category, ICategoryAutocompleteOptions> {

    private readonly _categoryClient = inject(CategoryApiClientService);

    /**
     * Get autocomplete suggestions for categories based on query and options
     *
     * @param query - User's search input
     * @param options - Autocomplete configuration including includeNone option
     * @returns Observable of filtered category autocomplete results
     */
    autocomplete$(query?: string, options?: ICategoryAutocompleteOptions): Observable<IAutocompleteResults<Category>> {
        return this._categoryClient.getAll$(new CategorySearchFilterOptions().setDefaults()).pipe(
            map((categories) => {
                let filteredCategories = categories;

                // Add "None" option if requested (for non-required fields)
                if (options?.includeNone) {
                    const noneCategory = new Category();
                    noneCategory.id = "0";
                    noneCategory.name = "None";
                    filteredCategories = [noneCategory, ...filteredCategories];
                }

                // Apply query filter (client-side)
                if (!StringUtil.emptyString(query, true)) {
                    const filterValue = StringUtil.sanitizeString(query).toLowerCase();
                    filteredCategories = filteredCategories.filter((category) =>
                        category.name.toLowerCase().includes(filterValue));
                }

                // Convert categories to autocomplete options
                const autocompleteOptions: Array<IAutocompleteOption<Category>> = filteredCategories.map((category) => ({
                    option: this.getAutocompleteDisplayName(category, options),
                    value: category
                }));

                return {
                    options: autocompleteOptions
                };
            })
        );
    }

    /**
     * Get display name for a category in autocomplete
     */
    getAutocompleteDisplayName(value?: Category, options?: ICategoryAutocompleteOptions): string {
        return value?.name || "";
    }

    /**
     * Returns a unique identifier for tracking items in the autocomplete
     */
    trackByIdentifier(index: number, result: IAutocompleteOption<Category>): any {
        return result?.value?.id;
    }

    /**
     * Returns true if the value does not exist in the items array
     */
    isUnique(value: Category, items: Array<Category>): boolean {
        return !items.some((item) => item.id === value.id);
    }
}
